import appTheme from "./theme/theme";
import imageCompression from "browser-image-compression";
import { v4 as uuidv4 } from "uuid";

const capitalizeFirstLetter = (input: unknown): string => {
  if (typeof input !== "string" || !input.trim()) {
    return "";
  }

  const trimmedInput = input.trim();
  return trimmedInput.charAt(0).toUpperCase() + trimmedInput.slice(1);
};

const getChangedFields = (initialValues: any, newValues: any) => {
  const changedFields: string[] = [];
  Object.keys(newValues).forEach((key) => {
    if (JSON.stringify(newValues[key]) !== JSON.stringify(initialValues[key])) {
      changedFields.push(key);
    }
  });
  return changedFields;
};

const compressImage = async (file: File) => {
  try {
    if (file.size <= 1024 * 1024) {
      return file;
    }

    const options = {
      maxSizeMB: 1,
      useWebWorker: true,
      initialQuality: 0.5,
      alwaysKeepResolution: false,
    };

    let compressedFile = await imageCompression(file, options);

    while (compressedFile.size > 1024 * 1024) {
      const retryOptions = { ...options, initialQuality: (options.initialQuality ?? 0.8) * 0.8 };
      compressedFile = await imageCompression(compressedFile, retryOptions);
    }
    return compressedFile;
  } catch (error) {
    console.error("Image compression error:", error);
    return file; 
  }
};

const TRIP_STATUS = {
  ImagesSlot_Pending: "Images/Slot Pending",
  IN_REVIEW: "In-Review",
  APPROVED: "Approved",
  REJECTED: "Rejected",
  RESUBMIT: "Resubmit",
  CLOSED: "Closed",
};

const SHORT_STATUS = {
  IN_REVIEW: "In-Review",
  APPROVED: "Approved",
  REJECTED: "Rejected",
};

const HOST_APPROVAL_STATUS = {
  PENDING: "PENDING",
  ACCEPTED: "ACCEPTED",
  REJECTED: "REJECTED",
};

const getStatusColor = (status: string) => {
  switch (status) {
    case TRIP_STATUS.APPROVED:
      return {
        color: "#17B26A",
        bgcolor: "#ECFDF3",
        textcolor: "#067647",
        borderColor: "#ABEFC6",
      };
    case TRIP_STATUS.IN_REVIEW:
      return {
        color: "#F79009",
        bgcolor: "#FFFAEB",
        textcolor: "#B54708",
        borderColor: "#FEDF89",
      };
    case TRIP_STATUS.REJECTED:
      return {
        color: "#F04438",
        bgcolor: "#FEF3F2",
        textcolor: "#B42318",
        borderColor: "#FECDCA",
      };
    case TRIP_STATUS.ImagesSlot_Pending:
      return {
        color: "#6B7280",
        bgcolor: "#E5E7EB",
        textcolor: "#374151",
        borderColor: "#D1D5DB",
      };
    case TRIP_STATUS.RESUBMIT:
      return {
        color: "#6366F1",
        bgcolor: "#EEF2FF",
        textcolor: "#4338CA",
        borderColor: "#C7D2FE",
      };
    default:
      return {
        color: "#9CA3AF",
        bgcolor: "#F3F4F6",
        textcolor: "#4B5563",
        borderColor: "#D1D5DB",
      };
  }
};

const getHostAcceptance = (status: string) => {
  switch (status) {
    case "ACCEPTED":
      return {
        color: "#17B26A",
        bgcolor: "#ECFDF3",
        textcolor: "#067647",
        borderColor: "#ABEFC6",
      };
    case "REJECTED":
      return {
        color: "#F04438",
        bgcolor: "#FEF3F2",
        textcolor: "#B42318",
        borderColor: "#FECDCA",
      };
    case "PENDING":
      return {
        color: "#6B7280",
        bgcolor: "#E5E7EB",
        textcolor: "#374151",
        borderColor: "#D1D5DB",
      };
    default:
      return {
        color: "#9CA3AF",
        bgcolor: "#F3F4F6",
        textcolor: "#4B5563",
        borderColor: "#D1D5DB",
      };
  }
};

const containsPropertyMerchant = (
  userDetails: any,
  excludingKey: string
): string => {
  if (!userDetails) return "FALSE"; // If no userDetails, return false

  const requiredKeys = [
    "organisationDetails",
    "merchantProfileDetails",
    "bankDetails",
    // "merchantKYC",
    "merchantBasicDetails",
    "vendorAgreement",
  ];

  const containsEveryField = requiredKeys.every((key) =>
    Object.hasOwn(userDetails, key)
  );

  if (containsEveryField) return "UPDATE";
  // Check if all required keys exist, except the one being added
  const val = requiredKeys.every(
    (key) => key === excludingKey || Object.hasOwn(userDetails, key)
  );
  return val ? "TRUE" : "FALSE";
};

const containsPropertyHost = (
  userDetails: any,
  excludingKey: string
): string => {
  if (!userDetails) return "FALSE"; // If no userDetails, return false

  const requiredKeys = ["hostKYC", "hostBasicDetails"];

  const containsEveryField = requiredKeys.every((key) =>
    Object.hasOwn(userDetails, key)
  );

  if (containsEveryField) return "UPDATE";

  const val = requiredKeys.every(
    (key) => key === excludingKey || Object.hasOwn(userDetails, key)
  );
  return val ? "TRUE" : "FALSE";
};

const ALLOWED_ROUTES: Record<string, string[]> = {
  merchant: [
    "/merchant-basic-details",
    // "/merchant-kyc-details",
    "/merchant-profile-details",
    "/bank-details",
    "/organisation-business-details",
    "/create-new-trips",
    "/trips-created",
    // "/slotwise-booking",
    "/individual-booking",
    "/support",
    "/vendor-agreement",
    "/memories-list",
    "/trip-reviews",
  ],
  host: ["/basic-details", "/kyc", "/profile-details", "/trips", "/support"],
};

const arrayBufferToFile = (
  buffer: ArrayBuffer,
  fileName: string,
  mimeType = "image/png"
) => {
  const blob = new Blob([buffer], { type: mimeType });
  return new File([blob], fileName, { type: mimeType });
};

const format = (val: number | string) => {
  const date = new Date(val);

  const day = String(date.getDate()).padStart(2, "0");
  const month = String(date.getMonth() + 1).padStart(2, "0"); // Months are zero-based
  const year = date.getFullYear();

  const hours = String(date.getHours()).padStart(2, "0");
  const minutes = String(date.getMinutes()).padStart(2, "0");

  return `${day}-${month}-${year} ${hours}:${minutes}`;
};

const getSerialNumber = (index: number, page: number, pageSize = 10) => {
  return index + (page - 1) * pageSize + 1;
};

const formatBrandName = (name: string) => {
  return name.toLowerCase().replace(/\b\w/g, (char) => char.toUpperCase());
};

const getS3FileUrl = (folderName: string, fileName: string) =>
  `https://d3thbee5ims1p2.cloudfront.net/${folderName}/${fileName}`;

const AMITIES: string[] = [
  "Cozy Beds",
  "Blankets",
  "Charging Points",
  "Fan/Heater",
  "AC",
  "Lockable Storage",
  "Clean Washrooms",
  "Hot Water",
  "Changing Rooms",
  "Towels & Toiletries",
  "Dining Area",
  "Filtered Drinking Water",
  "Tea/Coffee Station",
  "BBQ Setup",
  "Wi-Fi",
  "Bonfire",
  "Pool Table",
  "Lounge/Seating Area",
  "Board Games",
  "Nature Trails",
  "Parking",
  "First Aid Kit",
  "Luggage Storage",
  "Pet-Friendly",
  "Adventure Activities",
  "Wheelchair Access",
  "24/7 Security Staff",
  "CCTV in common areas",
  "Yoga/Meditation Zone",
  "Insect Repellent",
  "Stargazing Setup",
  "Laundry Service",
];

const ACCOMMODATION_TYPES = [
  "Camp/Tent",
  "Dorm",
  "Hotel",
  "HomeStay",
  "Guest House",
  "Villa",
  "Other",
] as const;

const generateId = (): string => {
  return Math.random().toString(36).substring(2, 15);
};

/**
 * Formats a price (string or number) to a string fixed to 2 decimals.
 * Returns '0.00' if input cannot be parsed.
 * Optionally, pass the "addCurrencySymbol" flag.
 */
function formatPrice(
  input: string | number | null | undefined,
  addCurrencySymbol = false
): string {
  if (input === null || input === undefined || input === "") {
    return addCurrencySymbol ? "₹0.00" : "0.00";
  }

  const price = Number(input);
  if (isNaN(price)) {
    return addCurrencySymbol ? "₹0.00" : "0.00";
  }

  return (addCurrencySymbol ? "₹" : "") + price.toFixed(2);
}


/**
 * Generate a RFC4122 v4 UUID string.
 *
 * @returns {string} uuid v4
 *
 * Usage:
 *   import { generateUid } from "@/utils/uuid";
 *   const id = generateUid();
 */
const generateUid = (): string => {
  return uuidv4();
};

/**
 * Gets video duration in seconds
 */
const getVideoDuration = (file: File): Promise<number> => {
  return new Promise((resolve, reject) => {
    const url = URL.createObjectURL(file)
    const v = document.createElement("video")
    v.preload = "metadata"
    v.src = url

    const cleanup = () => {
      URL.revokeObjectURL(url)
      v.remove()
    }

    v.onloadedmetadata = () => {
      cleanup()
      resolve(v.duration)
    }

    v.onerror = () => {
      cleanup()
      reject(new Error("Unable to determine video duration"))
    }
  })
}


/**
 * Validates video file - checks size and duration
 * @param file Video file to validate
 * @param maxSizeMB Max size in MB (default 25)
 * @param maxSeconds Max duration in seconds (default 90)
 */
const validateVideoFile = async (
  file: File,
  maxSizeMB = 25,
  maxSeconds = 90,
): Promise<{ valid: boolean; error?: string; duration?: number }> => {
  const maxSizeBytes = maxSizeMB * 1024 * 1024

  if (!file.type.startsWith("video/")) {
    return { valid: false, error: "File must be a video" }
  }

  if (file.size > maxSizeBytes) {
    return {
      valid: false,
      error: `Video too large. Max ${maxSizeMB}MB.`,
    }
  }

  try {
    const duration = await getVideoDuration(file)
    if (duration > maxSeconds) {
      return {
        valid: false,
        error: `Video too long (${Math.round(duration)}s). Max ${maxSeconds}s.`,
      }
    }
    return { valid: true, duration }
  } catch (err: any) {
    return {
      valid: false,
      error: err?.message || "Could not determine video duration",
    }
  }
}

export {
  appTheme,
  capitalizeFirstLetter,
  getChangedFields,
  compressImage,
  TRIP_STATUS,
  SHORT_STATUS,
  getStatusColor,
  containsPropertyMerchant,
  containsPropertyHost,
  ALLOWED_ROUTES,
  getHostAcceptance,
  HOST_APPROVAL_STATUS,
  arrayBufferToFile,
  format,
  getSerialNumber,
  formatBrandName,
  getS3FileUrl,
  AMITIES,
  ACCOMMODATION_TYPES,
  generateId,
  formatPrice,
  generateUid,
  validateVideoFile,
  getVideoDuration,
};
